import React, { useState } from "react";

import styled from "styled-components";

import Button from "components/atoms/button";
import RadioButton from "components/atoms/input/RadioButton";

import {
    ActionGroup,
    ActionRow,
    FormSection,
    FormSectionHeader,
    FormSectionTitle,
    HelperText,
    InitialFieldBody,
    InitialFieldCard,
    InitialFieldChevron,
    InitialFieldHeader,
    InitialFieldPrompt,
    InitialFieldSummary,
    InlineInput,
    InlineInputRow,
    InputWrap,
    OptionDescription,
    OptionTextWrap,
    PanelCard,
    PanelHeader,
    PanelHeaderAside,
    PanelHeaderMain,
    PanelTitle,
    QueryInput,
    QuestionOptionRow,
} from "components/organisms/report/styled";

import { genericEnterKeyDown } from "utils/validation";

const ReadyStatus = styled.div`
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    background: ${(props) => (props.$ready ? "#dcfce7" : "#f1f5f9")};
    color: ${(props) => (props.$ready ? "#166534" : "#475569")};
    font-size: 1.2rem;
    font-weight: 600;
`;

export default function RequestStepSection({
    ready,
    queryInput,
    onQueryInputChange,
    initialFields,
    initialSelections,
    initialCustomInputs,
    onSelectInitialOption,
    onChangeInitialCustomInput,
    onSubmitRequest,
    canSubmitRequest,
}) {
    const [expandedCategory, setExpandedCategory] = useState("");

    const toggleCategory = (category) => {
        setExpandedCategory((prev) => (prev === category ? "" : category));
    };

    return (
        <PanelCard>
            <PanelHeader>
                <PanelHeaderMain>
                    <PanelTitle>연구 주제 입력</PanelTitle>
                    <HelperText>주제와 기본 조건을 정하면 에이전트가 먼저 분석 계획을 작성합니다.</HelperText>
                </PanelHeaderMain>
                <PanelHeaderAside>
                    <ReadyStatus $ready={ready}>{ready ? "연결됨" : "연결 중"}</ReadyStatus>
                </PanelHeaderAside>
            </PanelHeader>

            <FormSection>
                <FormSectionHeader>
                    <FormSectionTitle>연구 주제</FormSectionTitle>
                </FormSectionHeader>
                <InputWrap>
                    <QueryInput
                        value={queryInput}
                        onChange={(e) => onQueryInputChange(e.target.value)}
                        onKeyDown={(e) => {
                            genericEnterKeyDown(e, () => {
                                if (canSubmitRequest) {
                                    onSubmitRequest();
                                }
                            });
                        }}
                        placeholder="에이전트가 검색할 연구 주제를 입력하세요."
                        disabled={!ready}
                    />
                </InputWrap>
            </FormSection>

            <FormSection>
                <FormSectionHeader>
                    <FormSectionTitle>기본 조건</FormSectionTitle>
                    <HelperText>기본값으로 시작해도 됩니다. 필요한 항목만 펼쳐서 조정하세요.</HelperText>
                </FormSectionHeader>
                {initialFields.map((field) => {
                    const selectedOptionId = initialSelections[field.category];
                    const selectedOption =
                        field.options.find((option) => option.id === selectedOptionId) || field.options[0];
                    const selectedSummaryDescription =
                        selectedOption?.id === "other"
                            ? (initialCustomInputs[field.category] || "").trim() || selectedOption?.description
                            : selectedOption?.description;
                    const isExpanded = expandedCategory === field.category;
                    return (
                        <InitialFieldCard key={field.category} $collapsible $expanded={isExpanded}>
                            <InitialFieldHeader
                                type="button"
                                onClick={() => toggleCategory(field.category)}
                                aria-expanded={isExpanded}
                                aria-controls={`initial-field-body-${field.category}`}
                            >
                                <div>
                                    <InitialFieldPrompt>{field.prompt}</InitialFieldPrompt>
                                    <InitialFieldSummary>
                                        {selectedOption?.label}
                                        {selectedSummaryDescription ? ` - ${selectedSummaryDescription}` : ""}
                                    </InitialFieldSummary>
                                </div>
                                <InitialFieldChevron $expanded={isExpanded}>⌄</InitialFieldChevron>
                            </InitialFieldHeader>

                            {isExpanded && (
                                <InitialFieldBody id={`initial-field-body-${field.category}`}>
                                    <div>
                                        {field.options.map((option) => (
                                            <QuestionOptionRow key={`${field.category}-${option.id}`}>
                                                <RadioButton
                                                    type="radio"
                                                    name={`initial-${field.category}`}
                                                    label={option.label}
                                                    checked={selectedOptionId === option.id}
                                                    onChange={() => {
                                                        onSelectInitialOption(field.category, option.id);
                                                        if (option.id !== "other") {
                                                            setExpandedCategory("");
                                                        }
                                                    }}
                                                    disabled={!ready}
                                                />
                                                <OptionTextWrap>
                                                    <OptionDescription>{option.description}</OptionDescription>
                                                </OptionTextWrap>
                                            </QuestionOptionRow>
                                        ))}
                                    </div>
                                    {selectedOptionId === "other" && (
                                        <InlineInputRow>
                                            <InlineInput
                                                value={initialCustomInputs[field.category] || ""}
                                                onChange={(e) =>
                                                    onChangeInitialCustomInput(field.category, e.target.value)
                                                }
                                                placeholder={field.customPlaceholder}
                                                disabled={!ready}
                                            />
                                        </InlineInputRow>
                                    )}
                                </InitialFieldBody>
                            )}
                        </InitialFieldCard>
                    );
                })}
            </FormSection>

            <ActionRow>
                <HelperText>Enter로도 계획 생성을 시작할 수 있습니다.</HelperText>
                <ActionGroup>
                    <Button
                        onlyText
                        width={"140px"}
                        height={"44px"}
                        size={"small"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontColor={"var(--color-White)"}
                        buttonText={"계획 시작"}
                        onClick={onSubmitRequest}
                        disabled={!canSubmitRequest}
                    />
                </ActionGroup>
            </ActionRow>
        </PanelCard>
    );
}
